/**
 * Bridges palette groups to the sidebar search field: one matcher for both, so
 * "/" in a section and Mod+K in the strip rank the same thing the same way.
 *
 * @module components/sidebar/paletteSidebarSearch
 */
import type { ReactNode } from "react";

import {
  filterCommandPaletteGroups,
  type CommandPaletteActionItem,
  type CommandPaletteGroup,
} from "../CommandPalette.logic";
import type { SidebarSearchResult } from "./SidebarSearch";

export interface PaletteSidebarResult extends SidebarSearchResult {
  readonly run: () => Promise<void>;
}

/** Flat, ranked results for `query`, each tagged with the group it came from. */
export function searchPaletteGroups(
  groups: ReadonlyArray<CommandPaletteGroup>,
  query: string,
): PaletteSidebarResult[] {
  return filterCommandPaletteGroups({
    activeGroups: [],
    query,
    isInSubmenu: false,
    projectSearchItems: [],
    threadSearchItems: [],
    globalSearchGroups: groups,
  }).flatMap((group) =>
    group.items
      .filter((item): item is CommandPaletteActionItem => item.kind === "action")
      .map((item) => ({
        id: item.value,
        title: typeof item.title === "string" ? item.title : item.value,
        detail: [group.label, item.description].filter(Boolean).join(" · "),
        icon: item.icon as ReactNode,
        run: item.run,
      })),
  );
}

/**
 * The chat sidebar's search: thread titles, every word required, a title that
 * starts with the query first, then most recent first.
 */
export function searchThreadTitles<Thread extends { readonly title: string }>(
  threads: ReadonlyArray<Thread>,
  query: string,
  limit = 30,
): Thread[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  const needle = tokens.join(" ");
  return threads
    .map((thread, index) => ({ thread, index, title: thread.title.toLowerCase() }))
    .filter(({ title }) => tokens.every((token) => title.includes(token)))
    .toSorted(
      (left, right) =>
        Number(right.title.startsWith(needle)) - Number(left.title.startsWith(needle)) ||
        left.index - right.index,
    )
    .slice(0, limit)
    .map(({ thread }) => thread);
}
